import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle, ArrowRight, Phone, Users, Clock, Mail, MessageCircle,
         Wrench, Hammer, Zap, ArrowUpDown, ShieldCheck, ClipboardCheck, UserCheck,
         FileText } from "lucide-react";
import ContactModal from "../components/ContactModal";
import Seo from "../components/Seo";
import { filArianeJsonLd } from "../utils/donneesStructurees";
import ChampAntiRobot from "../components/ChampAntiRobot";
import { trackFormSubmit } from "../utils/analytics";
import { attributionCourante } from "../utils/attribution";

const AccesPro = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    nomEntreprise: '',
    siret: '',
    nomContact: '',
    email: '',
    telephone: '',
    adresse: '',
    specialites: [],
    zoneIntervention: '',
    experience: '',
    certifications: '',
    assurance: '',
    message: '',
    website: ''
  });

  const specialitesList = [
    'Salle de bain PMR',
    'Monte-escaliers',
    'Volets roulants électriques',
    'Rampes d\'accès',
    'Élargissement portes',
    'Éclairage sécurisé',
    'Sol antidérapant',
    'Barres d\'appui',
    'Autres adaptations PMR'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSpecialiteChange = (specialite) => {
    setFormData(prev => ({
      ...prev,
      specialites: prev.specialites.includes(specialite)
        ? prev.specialites.filter(s => s !== specialite)
        : [...prev.specialites, specialite]
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Préparer les données pour l'API
      const apiData = {
        nom_entreprise: formData.nomEntreprise,
        siret: formData.siret,
        nom_contact: formData.nomContact,
        email: formData.email,
        telephone: formData.telephone,
        adresse: formData.adresse,
        specialites: formData.specialites,
        zone_intervention: formData.zoneIntervention,
        experience: formData.experience,
        certifications: formData.certifications,
        assurance: formData.assurance,
        message: formData.message,
        website: formData.website
      };

      // Appel API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-partenaire-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...apiData, attribution: attributionCourante() }),
      });

      const result = await response.json();

      if (result.success) {
        trackFormSubmit('partenariat');
        setSubmitStatus({
          type: 'success',
          message: 'Votre demande de partenariat a été envoyée avec succès ! Nous vous contacterons sous 48h.'
        });
        // Reset du formulaire
        setFormData({
          nomEntreprise: '', siret: '', nomContact: '', email: '', telephone: '', 
          adresse: '', specialites: [], zoneIntervention: '', experience: '',
          certifications: '', assurance: '', message: '', website: ''
        });
      } else {
        throw new Error(result.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur envoi formulaire:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Erreur lors de l\'envoi. Veuillez réessayer ou nous contacter directement.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------
  // Dépôt de dossier client
  //
  // Formulaire distinct de la candidature partenaire : l'artisan qui a déjà
  // un client n'a pas à remplir SIRET, assurance et spécialités pour nous
  // confier un dossier. État et envoi sont donc séparés, y compris le champ
  // piège — les deux formulaires cohabitent dans la même page.
  // ---------------------------------------------------------------------
  const TYPES_TRAVAUX = [
    { valeur: 'salle-de-bain', libelle: 'Salle de bain / douche de plain-pied' },
    { valeur: 'monte-escalier', libelle: 'Monte-escalier' },
    { valeur: 'exterieurs', libelle: 'Extérieurs (accès, rampe, seuils)' },
    { valeur: 'autre', libelle: 'Autre adaptation du logement' },
  ];

  // Les six départements où nous sommes habilités AMO. Le 78 et le 94 n'y
  // sont pas : les proposer produirait des dossiers que nous refuserions.
  const DEPARTEMENTS = [
    { valeur: '75', libelle: 'Paris (75)' },
    { valeur: '77', libelle: 'Seine-et-Marne (77)' },
    { valeur: '91', libelle: 'Essonne (91)' },
    { valeur: '92', libelle: 'Hauts-de-Seine (92)' },
    { valeur: '93', libelle: 'Seine-Saint-Denis (93)' },
    { valeur: '95', libelle: "Val-d'Oise (95)" },
  ];

  const DOSSIER_VIDE = {
    nomClient: '',
    typeTravaux: '',
    departement: '',
    telephone: '',
    email: '',
    message: '',
    website: '',
  };

  const [dossierData, setDossierData] = useState(DOSSIER_VIDE);
  const [dossierEnvoi, setDossierEnvoi] = useState(false);
  const [dossierStatut, setDossierStatut] = useState(null);

  const handleDossierChange = (e) => {
    const { name, value } = e.target;
    setDossierData(prev => ({ ...prev, [name]: value }));
  };

  const handleDossierSubmit = async (e) => {
    e.preventDefault();
    setDossierEnvoi(true);
    setDossierStatut(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/send-dossier-artisan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attribution: attributionCourante(),
          nom_client: dossierData.nomClient,
          type_travaux: dossierData.typeTravaux,
          departement: dossierData.departement,
          telephone: dossierData.telephone,
          email: dossierData.email,
          message: dossierData.message,
          website: dossierData.website,
        }),
      });

      const result = await response.json();

      if (result.success) {
        trackFormSubmit('dossier-artisan');
        setDossierStatut({
          type: 'success',
          message: result.message || "Dossier transmis. Nous vous rappelons sous 48 h ouvrées.",
        });
        setDossierData(DOSSIER_VIDE);
      } else {
        throw new Error(result.message || "Erreur lors de l'envoi");
      }
    } catch (error) {
      console.error('Erreur envoi dossier artisan:', error);
      setDossierStatut({
        type: 'error',
        message: "Erreur lors de l'envoi. Réessayez ou appelez-nous au 09 39 03 54 44.",
      });
    } finally {
      setDossierEnvoi(false);
    }
  };

  // Les trois arguments qui décident un artisan à répondre. Chacun décrit ce
  // que nous faisons réellement : un argument invérifiable se retourne au
  // premier chantier, et l'artisan déçu ne revient pas.
  const avantages = [
    {
      icon: <ClipboardCheck className="h-8 w-8 text-green-700" />,
      titre: "Vos dossiers MaPrimeAdapt' traités de A à Z",
      description:
        "Vous nous confiez le client, nous vérifions son éligibilité, réalisons le diagnostic autonomie et montons le dossier ANAH.",
      detail:
        "Vous n'avez pas de formulaire à remplir ni de pièce à réunir : le dossier est instruit sans vous mobiliser.",
    },
    {
      icon: <UserCheck className="h-8 w-8 text-blue-600" />,
      titre: "Un interlocuteur unique par chantier",
      description:
        "Le même AMO suit le dossier du premier appel jusqu'au versement de l'aide, et reste votre contact direct.",
      detail:
        "Vous n'avez pas à courir après l'ANAH ni à expliquer le dossier à quelqu'un de nouveau à chaque étape.",
    },
    {
      icon: <ShieldCheck className="h-8 w-8 text-orange-700" />,
      titre: "Le financement est validé avant le premier coup de pioche",
      description:
        "Le montant de l'aide est notifié par l'ANAH avant le démarrage des travaux. Vous savez que le client a les moyens de son projet.",
      detail:
        "L'aide est versée après réception des travaux, sur facture. Ce n'est pas l'ANAH qui vous règle : c'est le client, comme sur n'importe quel chantier.",
    },
  ];

  // Métiers effectivement mobilisés par les travaux MaPrimeAdapt'.
  const profils = [
    {
      icon: <Wrench className="h-7 w-7 text-green-700" />,
      metier: "Plombiers et carreleurs",
      travaux: "Douches de plain-pied, remplacement de baignoire, sols antidérapants, barres d'appui.",
      volume: "Le poste le plus demandé de nos dossiers.",
    },
    {
      icon: <Hammer className="h-7 w-7 text-green-700" />,
      metier: "Menuisiers",
      travaux: "Élargissement de portes, seuils, rampes d'accès, volets roulants motorisés.",
      volume: "Souvent en second œuvre sur un chantier salle de bain.",
    },
    {
      icon: <Zap className="h-7 w-7 text-green-700" />,
      metier: "Électriciens",
      travaux: "Éclairage sécurisé, interrupteurs accessibles, motorisation, détecteurs de chute.",
      volume: "Interventions courtes, souvent groupées.",
    },
    {
      icon: <ArrowUpDown className="h-7 w-7 text-green-700" />,
      metier: "Installateurs de monte-escalier",
      travaux: "Monte-escalier droit ou courbe, plateformes élévatrices.",
      volume: "Chantiers à ticket élevé, demande soutenue en pavillonnaire.",
    },
  ];

  const faqItems = [
    {
      question: "Quelles qualifications faut-il pour devenir partenaire ?",
      reponse: "Aucune qualification spécifique n'est exigée par MaPrimeAdapt'. Nous demandons cependant les justificatifs d'assurance, SIRET valide et références qualité. Les certifications RGE, Handibat ou Silverbat sont un plus."
    },
    {
      question: "Quel est le délai moyen d'instruction ANAH en 2026 ?",
      reponse: "Les délais varient selon les départements, mais nous observons en moyenne 2-3 mois d'instruction ANAH après dépôt du dossier complet."
    },
    {
      question: "Y a-t-il des frais ou commissions pour être partenaire ?",
      reponse: "Non, le partenariat est totalement gratuit. Nous ne prenons aucune commission sur vos devis. Notre rémunération vient exclusivement de l'AMO pris en charge par MaPrimeAdapt'."
    },
    {
      question: "Est-ce que vous gardez le client, ou est-ce qu'il reste le mien ?",
      reponse: "Le client reste le vôtre. Nous intervenons sur le dossier MaPrimeAdapt' — éligibilité, diagnostic autonomie, dépôt ANAH, suivi jusqu'au versement — et vous réalisez les travaux. Nous ne le mettons pas en relation avec d'autres artisans et nous ne prenons aucune commission sur vos devis."
    },
    {
      question: "Qui me paie, et quand ?",
      reponse: "Votre client vous règle, comme sur n'importe quel chantier : l'ANAH ne verse rien directement à l'artisan. Ce que change MaPrimeAdapt', c'est que le montant de l'aide est notifié par écrit avant le démarrage des travaux, et que l'aide est versée au client sur présentation de vos factures une fois les travaux réceptionnés. Le plan de financement est donc connu de tous avant que vous n'engagiez quoi que ce soit."
    },
    {
      question: "Puis-je démarrer le chantier dès la signature du devis ?",
      reponse: "Non, et c'est le point sur lequel nous sommes les plus fermes. Aucun acompte, aucune livraison de matériel, aucune journée de chantier avant l'accord écrit de l'ANAH : des travaux commencés avant la notification font perdre l'aide au client, définitivement. Nous vous prévenons dès que l'accord est arrivé."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Devenir artisan partenaire MaPrimeAdapt' Île-de-France"
        description="Plombier, menuisier, électricien ou installateur de monte-escalier en Île-de-France ? Recevez des chantiers MaPrimeAdapt' déjà instruits, sans commission. AMO habilité ANAH, 289 dossiers montés."
        chemin="/acces-pro"
        donnees={[
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Espace artisans", url: "/acces-pro" },
          ]),
        ]}
      />
      <Header />

      <main id="contenu">
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-gray-50 to-white py-20">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center px-4 py-2 bg-green-700 text-white rounded-full text-sm font-medium">
                  <Users className="h-4 w-4 mr-2" />
                  Réseau Artisans Experts PMR
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  Rejoignez notre réseau d&apos;artisans{" "}
                  {/* L'espace ci-dessus est indispensable : le span est en
                      block, donc l'espace ne se voit pas à l'écran, mais sans
                      lui le texte lu par Google et les lecteurs d'écran donne
                      « artisanspartenaires ». */}
                  <span className="text-green-700 block mt-2">partenaires MaPrimeAdapt&apos;</span>
                </h1>

                {/* Accroche réécrite : la version précédente laissait entendre que
                    nous apportions des clients aux artisans, ce qui n'est pas notre
                    métier et décevait à la première demande. Ce que nous prenons en
                    charge, c'est le dossier ANAH du client que l'artisan a déjà. */}
                <p className="text-xl text-gray-900 leading-relaxed">
                  Vous intervenez chez des clients éligibles à MaPrimeAdapt&apos; ?{" "}
                  <strong>TEF prend en charge l&apos;intégralité du dossier ANAH pour vos
                  chantiers</strong> — visite terrain, conformité, suivi administratif et
                  coordination jusqu&apos;au versement des aides. Vos clients avancent
                  sereinement, vous vous concentrez sur vos travaux.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-green-700 hover:bg-green-800 text-white shadow-lg group w-full sm:w-auto"
                  onClick={() => document.getElementById('deposer-dossier').scrollIntoView({ behavior: 'smooth' })}
                >
                  Déposer un dossier client
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-green-200 hover:bg-green-50 w-full sm:w-auto"
                onClick={() => setIsContactModalOpen(true)}
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Nous contacter
              </Button>
                
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-green-200 hover:bg-green-50 w-full sm:w-auto"
                  onClick={() => window.location.href = 'tel:0939035444'}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  09 39 03 54 44
                </Button>
              </div>

              <p className="text-sm text-gray-700">
                Pas encore de chantier en vue ?{" "}
                <button
                  type="button"
                  onClick={() => document.getElementById('formulaire-partenaire').scrollIntoView({ behavior: 'smooth' })}
                  className="text-green-700 underline hover:text-green-800 font-medium"
                >
                  Rejoindre le réseau d&apos;artisans partenaires
                </button>
              </p>

              {/* Chiffres repris de l'accueil, pour que le site n'annonce pas
                  deux volumes différents selon la page où l'on entre. */}
              <div className="grid grid-cols-3 gap-6 pt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">289</div>
                  <div className="text-sm text-gray-700">Dossiers montés</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">6</div>
                  <div className="text-sm text-gray-700">Départements franciliens habilités</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">0 €</div>
                  <div className="text-sm text-gray-700">De commission sur vos devis</div>
                </div>
              </div>
            </div>

            {/* Image hero */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
              <img 
                src="/images/acces-pro.webp"
                width="900"
                height="675"
                loading="eager" 
                alt="Artisans partenaires rénovation PMR" 
                className="w-full h-64 object-cover"
              />
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Pourquoi choisir notre réseau ?
                </h2>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Éligibilité vérifiée dès la réception du dossier</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Dossier ANAH monté et déposé par nos soins</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Aide notifiée avant le démarrage du chantier</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Aucune commission sur vos devis</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages du partenariat */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Ce que change un chantier MaPrimeAdapt&apos; monté par un AMO
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Le travail administratif est fait avant que vous n&apos;entriez dans le dossier
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {avantages.map((avantage, index) => (
              <Card key={index} className="border-2 hover:border-green-200 transition-all duration-300 text-center">
                <CardHeader>
                  <div className="mx-auto mb-4">
                    {avantage.icon}
                  </div>
                  <CardTitle className="text-xl text-gray-900">
                    {avantage.titre}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-3 font-medium">{avantage.description}</p>
                  <p className="text-gray-700 text-sm">{avantage.detail}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Profils recherchés */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Les métiers que nous recherchons
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                L&apos;adaptation d&apos;un logement mobilise rarement un seul corps de métier.
                Nous constituons des équipes par département.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {profils.map((profil, index) => (
                <Card key={index} className="border-2 hover:border-green-200 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-green-50 p-3 rounded-lg flex-shrink-0">
                        {profil.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {profil.metier}
                        </h3>
                        <p className="text-gray-700 mb-2">{profil.travaux}</p>
                        <p className="text-sm text-green-800 font-medium">{profil.volume}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-gray-700 mb-6 max-w-3xl mx-auto">
                Votre métier n&apos;est pas dans la liste mais vous intervenez sur l&apos;adaptation
                du logement ? Écrivez-nous quand même : la liste reflète nos besoins d&apos;aujourd&apos;hui,
                pas les seuls travaux finançables.
              </p>
              <Button
                size="lg"
                className="bg-green-700 hover:bg-green-800 text-white shadow-lg group"
                onClick={() => document.getElementById('formulaire-partenaire').scrollIntoView({ behavior: 'smooth' })}
              >
                Déposer ma candidature
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Ce à quoi nous nous engageons.
          Un témoignage d'artisan avait été envisagé ici. Publier un avis
          inventé sur un site commercial est une pratique commerciale
          trompeuse (article L121-2 du code de la consommation, renforcé en
          2022 sur les faux avis) — et le site s'est justement construit sur
          des chiffres vérifiables. En attendant d'en recueillir un vrai, ce
          bloc dit ce que nous nous engageons à faire : c'est vérifiable dès
          le premier chantier.

          Pour le remplacer par un vrai témoignage : demander à un partenaire
          actuel son accord écrit, son nom, son métier et sa ville, et
          reprendre la mise en forme de components/Testimonials.jsx. */}
      <section className="py-24 bg-gradient-to-br from-green-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Nos engagements envers nos partenaires
              </h2>
              <p className="text-xl text-gray-700">
                Quatre points sur lesquels vous pouvez nous juger dès le premier dossier
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                ["Aucune commission, jamais",
                 "Nous ne prélevons rien sur vos devis. Notre rémunération vient de la prestation d'AMO, prise en charge par MaPrimeAdapt'."],
                ["Vous fixez vos prix",
                 "Nous ne négocions pas vos tarifs et ne vous imposons pas de grille. Le devis est le vôtre, le client le signe."],
                ["Pas de chantier avant l'accord ANAH",
                 "Nous ne vous demanderons jamais de commencer, de livrer du matériel ou d'encaisser un acompte avant la notification écrite. Un chantier démarré trop tôt fait perdre l'aide au client."],
                ["Un refus reste un refus",
                 "Si un dossier n'est pas éligible, nous vous le disons plutôt que de vous envoyer chez le client pour un devis qui ne servira à rien."],
              ].map(([titre, texte], index) => (
                <div key={index} className="bg-white p-6 rounded-xl border-2 border-green-100">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-green-700 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{titre}</h3>
                      <p className="text-gray-700">{texte}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dépôt de dossier client
          Placé avant la candidature partenaire : l'artisan qui arrive ici a le
          plus souvent un chantier en cours, pas une demande d'adhésion. */}
      <section id="deposer-dossier" className="py-24 bg-white border-t-4 border-green-700">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-green-700 text-white rounded-full text-sm font-medium mb-6">
                <FileText className="h-4 w-4 mr-2" />
                Vous avez déjà le client
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Déposer un dossier client
              </h2>
              <p className="text-xl text-gray-700">
                Confiez-nous le montage du dossier MaPrimeAdapt&apos; de votre client :
                nous prenons la visite terrain, la conformité, le dépôt ANAH et le suivi
                jusqu&apos;au versement. Vous gardez votre chantier et votre client.
              </p>
            </div>

            <form onSubmit={handleDossierSubmit} className="space-y-6">
              <ChampAntiRobot
                id="website-dossier-artisan"
                valeur={dossierData.website}
                onChange={handleDossierChange}
              />

              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">Le chantier</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <label htmlFor="nomClient" className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du client *
                    </label>
                    <input
                      type="text"
                      name="nomClient"
                      id="nomClient"
                      value={dossierData.nomClient}
                      onChange={handleDossierChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="typeTravaux" className="block text-sm font-medium text-gray-700 mb-2">
                        Type de travaux *
                      </label>
                      <select
                        name="typeTravaux"
                        id="typeTravaux"
                        value={dossierData.typeTravaux}
                        onChange={handleDossierChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Choisir…</option>
                        {TYPES_TRAVAUX.map((t) => (
                          <option key={t.valeur} value={t.valeur}>{t.libelle}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="departement" className="block text-sm font-medium text-gray-700 mb-2">
                        Département du logement *
                      </label>
                      <select
                        name="departement"
                        id="departement"
                        value={dossierData.departement}
                        onChange={handleDossierChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">Choisir…</option>
                        {DEPARTEMENTS.map((d) => (
                          <option key={d.valeur} value={d.valeur}>{d.libelle}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Nous sommes habilités AMO sur ces six départements franciliens.
                    Pour un logement situé ailleurs, appelez-nous avant de déposer le dossier.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">Vous contacter</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="telephoneArtisan" className="block text-sm font-medium text-gray-700 mb-2">
                        Votre téléphone *
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        id="telephoneArtisan"
                        value={dossierData.telephone}
                        onChange={handleDossierChange}
                        required
                        placeholder="06 12 34 56 78"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="emailArtisan" className="block text-sm font-medium text-gray-700 mb-2">
                        Votre email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="emailArtisan"
                        value={dossierData.email}
                        onChange={handleDossierChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="messageDossier" className="block text-sm font-medium text-gray-700 mb-2">
                      Précisions sur le dossier
                    </label>
                    <textarea
                      name="message"
                      id="messageDossier"
                      value={dossierData.message}
                      onChange={handleDossierChange}
                      rows="4"
                      placeholder="Situation du client, travaux envisagés, échéance du chantier…"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </CardContent>
              </Card>

              {dossierStatut && (
                <div className={`text-center p-4 rounded-lg ${
                  dossierStatut.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {dossierStatut.message}
                </div>
              )}

              <div className="text-center">
                <Button
                  type="submit"
                  size="lg"
                  className="bg-green-700 hover:bg-green-800 text-white px-8 py-3 disabled:opacity-50"
                  disabled={dossierEnvoi}
                >
                  {dossierEnvoi ? (
                    <>
                      <div className="animate-spin mr-2 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-5 w-5" />
                      Déposer le dossier
                    </>
                  )}
                </Button>
                <p className="text-sm text-gray-700 mt-4">
                  Nous vous rappelons sous 48 h ouvrées. Aucun frais, aucune commission sur vos devis.
                </p>
                <p className="text-xs text-gray-500 mt-4">
                  Les informations recueillies via ce formulaire sont utilisées uniquement pour
                  instruire ce dossier. Pour en savoir plus sur la gestion de vos données, consultez
                  notre{' '}
                  <a href="/politique-confidentialite" className="text-orange-700 underline hover:text-orange-800">
                    Politique de confidentialité
                  </a>.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Formulaire de partenariat */}
      <section id="formulaire-partenaire" className="py-24 bg-white border-t-4 border-green-700">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-green-700 text-white rounded-full text-sm font-medium mb-6">
                <Users className="h-4 w-4 mr-2" />
                Candidature en 5 minutes
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Devenir artisan partenaire
              </h2>
              <p className="text-xl text-gray-700">
                Remplissez ce formulaire : nous revenons vers vous sous 48 h ouvrées pour un
                premier échange. Aucun engagement, aucun frais d&apos;inscription.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <ChampAntiRobot
                id="website-acces-pro"
                valeur={formData.website}
                onChange={handleInputChange}
              />
              {/* Informations entreprise */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">Informations entreprise</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="nomEntreprise" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom de l&apos;entreprise *
                      </label>
                      <input
                        type="text"
                        name="nomEntreprise"
                        id="nomEntreprise"
                        value={formData.nomEntreprise}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="siret" className="block text-sm font-medium text-gray-700 mb-2">
                        Numéro SIRET *
                      </label>
                      <input
                        type="text"
                        name="siret"
                        id="siret"
                        value={formData.siret}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="adresse" className="block text-sm font-medium text-gray-700 mb-2">
                      Adresse complète *
                    </label>
                    <textarea
                      name="adresse"
                      id="adresse"
                      value={formData.adresse}
                      onChange={handleInputChange}
                      required
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Contact */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label htmlFor="nomContact" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom du contact *
                      </label>
                      <input
                        type="text"
                        name="nomContact"
                        id="nomContact"
                        value={formData.nomContact}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        id="telephone"
                        value={formData.telephone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Spécialités */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">Spécialités PMR</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">Sélectionnez vos domaines d&apos;expertise :</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    {specialitesList.map((specialite, index) => (
                      <label key={index} className="flex items-center space-x-3 cursor-pointer py-2">
                        <input
                          type="checkbox"
                          checked={formData.specialites.includes(specialite)}
                          onChange={() => handleSpecialiteChange(specialite)}
                          className="h-5 w-5 flex-shrink-0 rounded border-gray-300 text-green-700 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">{specialite}</span>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Zone et expérience */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">Activité</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <label htmlFor="zoneIntervention" className="block text-sm font-medium text-gray-700 mb-2">
                      Zone d&apos;intervention *
                    </label>
                    <textarea
                      name="zoneIntervention"
                      id="zoneIntervention"
                      value={formData.zoneIntervention}
                      onChange={handleInputChange}
                      required
                      placeholder="Départements, villes ou rayons d'intervention..."
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                      Expérience en rénovation PMR
                    </label>
                    <textarea
                      name="experience"
                      id="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      placeholder="Nombre d'années, projets réalisés, références..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="certifications" className="block text-sm font-medium text-gray-700 mb-2">
                        Certifications / Labels
                      </label>
                      <input
                        type="text"
                        name="certifications"
                        id="certifications"
                        value={formData.certifications}
                        onChange={handleInputChange}
                        placeholder="RGE, Handibat, Silverbat, autres..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="assurance" className="block text-sm font-medium text-gray-700 mb-2">
                        Assurance responsabilité civile *
                      </label>
                      <input
                        type="text"
                        name="assurance"
                        id="assurance"
                        value={formData.assurance}
                        onChange={handleInputChange}
                        required
                        placeholder="Nom compagnie et numéro de police"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message / Présentation
                    </label>
                    <textarea
                      name="message"
                      id="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Présentez votre entreprise et votre motivation..."
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Messages de statut */}
              {submitStatus && (
                <div className={`text-center p-4 rounded-lg ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800' 
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              {/* Bouton d'envoi */}
              <div className="text-center">
                <Button 
                  type="submit"
                  size="lg" 
                  className="bg-green-700 hover:bg-green-800 text-white px-8 py-3 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin mr-2 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-5 w-5" />
                      Envoyer la demande de partenariat
                    </>
                  )}
                </Button>
                <p className="text-sm text-gray-700 mt-4">
                  Votre demande sera envoyée directement par email et traitée sous 48h ouvrées
                </p>
              </div>
			          {/* RGPD Notice */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Les informations recueillies via ce formulaire sont utilisées uniquement pour répondre à votre demande.
          Pour en savoir plus sur la gestion de vos données, consultez notre{' '}
          <a href="/politique-confidentialite" className="text-orange-700 underline hover:text-orange-800">
            Politique de confidentialité
          </a>.
        </p>
            </form>
          </div>
        </div>
      </section>

      {/* Parcours AMO MaPrimeAdapt' */}
      <section className="py-24 bg-gradient-to-br from-pink-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6" style={{color: '#e91e63'}}>
                Un AMO réactif et présent à vos côtés
              </h2>
              <h3 className="text-4xl font-bold" style={{color: '#e91e63'}}>
                tout au long du projet
              </h3>
              <p className="text-lg text-gray-700 mt-8 max-w-4xl mx-auto">
                Durant le projet, artisans et assistants à maîtrise d'ouvrage (AMO) travaillent ensemble pour adapter le logement des personnes en perte d'autonomie et leur permettre de toucher les aides de l'État.
              </p>
            </div>

            {/* Parcours en 6 étapes avec effet de vague */}
            <div className="relative py-16">
              {/* Ligne ondulée de connexion */}
              <div className="hidden lg:block absolute top-32 left-0 right-0 z-0">
                <svg viewBox="0 0 1200 100" className="w-full h-16">
                  <path 
                    d="M0,50 Q200,20 400,50 T800,50 Q1000,30 1200,50" 
                    stroke="#e5e7eb" 
                    strokeWidth="2" 
                    fill="none"
                    strokeDasharray="8,4"
                  />
                </svg>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 relative z-10">
                {/* Étape 1 - Validation et éligibilité */}
                <div className="text-center">
                  <div className="relative mb-6">
                    <div 
                      className="w-24 h-16 text-white flex items-center justify-center text-2xl font-bold mx-auto shadow-lg transform rotate-12"
                      style={{
                        backgroundColor: '#4ade80',
                        borderRadius: '50%',
                        clipPath: 'ellipse(80% 100% at 50% 50%)'
                      }}
                    >
                      1
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Validation
                  </h4>
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    éligibilité du projet
                  </h4>
                  <div className="text-xl font-bold" style={{color: '#e91e63'}}>
                    72 heures
                  </div>
                </div>

                {/* Étape 2 - Visite à domicile */}
                <div className="text-center">
                  <div className="relative mb-6">
                    <div 
                      className="w-24 h-16 text-white flex items-center justify-center text-2xl font-bold mx-auto shadow-lg transform -rotate-6"
                      style={{
                        backgroundColor: '#22c55e',
                        borderRadius: '50%',
                        clipPath: 'ellipse(80% 100% at 50% 50%)'
                      }}
                    >
                      2
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    Visite à domicile
                  </h4>
                  <div className="text-xl font-bold" style={{color: '#e91e63'}}>
                    72 heures
                  </div>
                </div>

                {/* Étape 3 - Diagnostic */}
                <div className="text-center">
                  <div className="relative mb-6">
                    <div 
                      className="w-24 h-16 text-white flex items-center justify-center text-2xl font-bold mx-auto shadow-lg transform rotate-6"
                      style={{
                        backgroundColor: '#14b8a6',
                        borderRadius: '50%',
                        clipPath: 'ellipse(80% 100% at 50% 50%)'
                      }}
                    >
                      3
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">
                    Diagnostic
                  </h4>
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    Autonomie Logement
                  </h4>
                  <div className="text-xl font-bold" style={{color: '#e91e63'}}>
                    8 jours
                  </div>
                </div>

                {/* Étape 4 - Dépôt dossier */}
                <div className="text-center">
                  <div className="relative mb-6">
                    <div 
                      className="w-24 h-16 text-white flex items-center justify-center text-2xl font-bold mx-auto shadow-lg transform -rotate-8"
                      style={{
                        backgroundColor: '#0891b2',
                        borderRadius: '50%',
                        clipPath: 'ellipse(80% 100% at 50% 50%)'
                      }}
                    >
                      4
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    Dépôt dossier
                  </h4>
                  <div className="text-xl font-bold" style={{color: '#e91e63'}}>
                    72 heures
                  </div>
                </div>

                {/* Étape 5 - Validation ANAH */}
                <div className="text-center">
                  <div className="relative mb-6">
                    <div 
                      className="w-24 h-16 text-white flex items-center justify-center text-2xl font-bold mx-auto shadow-lg transform rotate-4"
                      style={{
                        backgroundColor: '#2563eb',
                        borderRadius: '50%',
                        clipPath: 'ellipse(80% 100% at 50% 50%)'
                      }}
                    >
                      5
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    Validation ANAH
                  </h4>
                  <div className="text-xl font-bold" style={{color: '#e91e63'}}>
                    3 mois*
                  </div>
                </div>

                {/* Étape 6 - Réalisation travaux */}
                <div className="text-center">
                  <div className="relative mb-6">
                    {/* Flèche décorative : masquée sur mobile où les étapes s'empilent */}
                    <div className="hidden sm:block absolute -left-8 top-1/2 transform -translate-y-1/2">
                      <ArrowRight className="h-6 w-6 text-gray-400" />
                    </div>
                    <div 
                      className="w-24 h-16 text-white flex items-center justify-center text-2xl font-bold mx-auto shadow-lg"
                      style={{
                        backgroundColor: '#1e40af',
                        borderRadius: '50%',
                        clipPath: 'ellipse(80% 100% at 50% 50%)'
                      }}
                    >
                      6
                    </div>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    Réalisation des travaux
                  </h4>
                  <div className="text-xl font-bold" style={{color: '#e91e63'}}>
                    5 mois*
                  </div>
                </div>
              </div>

              {/* Note délais */}
              <div className="text-center mt-8">
                <p className="text-sm text-gray-700">
                  *délais moyens constatés
                </p>
              </div>

              {/* Footer attribution */}
              <div className="text-center mt-12">
                <p className="text-sm text-gray-700 italic">
                  Étapes clés et délais moyens dans le cadre d'un accompagnement MaPrimeAdapt par Transition Energie France
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Carte des habilitations ANAH */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                La carte de nos habilitations ANAH
              </h2>
              <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                Nous pouvons accompagner tous les bénéficiaires éligibles à MaPrimeAdapt' qui se trouvent dans les départements 
                dans lesquels nous sommes habilités par l'ANAH.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Carte IDF + Oise */}
              <div className="relative bg-gradient-to-br from-blue-50 to-indigo-100 p-8 rounded-2xl">
                <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">
                  Île-de-France + Oise
                </h3>
                
                {/* Carte réaliste SVG */}
                <div className="flex justify-center">
                  <svg width="450" height="380" viewBox="0 0 450 380" className="max-w-full">
                    
                    {/* 60 - Oise (ajouté en haut) */}
                    <path
                      d="M180 20 L280 25 L300 45 L290 80 L250 75 L200 70 L160 50 Z"
                      fill="#f59e0b"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="235" y="50" textAnchor="middle" className="fill-white text-xs font-bold">60</text>
                    <text x="235" y="62" textAnchor="middle" className="fill-white text-xs">Oise</text>

                    {/* 95 - Val-d'Oise */}
                    <path
                      d="M160 80 L290 85 L280 120 L250 130 L200 125 L150 115 Z"
                      fill="#22c55e"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="220" y="105" textAnchor="middle" className="fill-white text-xs font-bold">95</text>
                    <text x="220" y="117" textAnchor="middle" className="fill-white text-xs">Val-d'Oise</text>

                    {/* 78 - Yvelines */}
                    <path
                      d="M50 140 L150 135 L160 180 L140 220 L80 215 L40 200 L30 160 Z"
                      fill="#f59e0b"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="100" y="175" textAnchor="middle" className="fill-white text-xs font-bold">78</text>
                    <text x="100" y="187" textAnchor="middle" className="fill-white text-xs">Yvelines</text>

                    {/* 92 - Hauts-de-Seine */}
                    <path
                      d="M160 150 L200 145 L210 175 L190 190 L150 185 Z"
                      fill="#22c55e"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="180" y="167" textAnchor="middle" className="fill-white text-xs font-bold">92</text>
                    <text x="180" y="179" textAnchor="middle" className="fill-white text-xs">H.-de-Seine</text>

                    {/* 75 - Paris */}
                    <path
                      d="M210 155 L240 150 L250 175 L235 185 L210 180 Z"
                      fill="#22c55e"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="230" y="167" textAnchor="middle" className="fill-white text-xs font-bold">75</text>
                    <text x="230" y="179" textAnchor="middle" className="fill-white text-xs">Paris</text>

                    {/* 93 - Seine-Saint-Denis */}
                    <path
                      d="M250 130 L320 135 L330 165 L310 180 L280 175 L250 170 Z"
                      fill="#22c55e"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="290" y="152" textAnchor="middle" className="fill-white text-xs font-bold">93</text>
                    <text x="290" y="164" textAnchor="middle" className="fill-white text-xs">S.-Saint-Denis</text>

                    {/* 94 - Val-de-Marne */}
                    <path
                      d="M210 190 L280 185 L290 220 L270 240 L220 235 L190 220 Z"
                      fill="#f59e0b"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="245" y="212" textAnchor="middle" className="fill-white text-xs font-bold">94</text>
                    <text x="245" y="224" textAnchor="middle" className="fill-white text-xs">Val-de-Marne</text>

                    {/* 77 - Seine-et-Marne */}
                    <path
                      d="M330 150 L420 155 L430 220 L410 280 L350 275 L320 250 L310 200 Z"
                      fill="#22c55e"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="370" y="215" textAnchor="middle" className="fill-white text-xs font-bold">77</text>
                    <text x="370" y="227" textAnchor="middle" className="fill-white text-xs">Seine-et-Marne</text>

                    {/* 91 - Essonne */}
                    <path
                      d="M140 240 L270 245 L280 300 L250 340 L180 335 L120 320 L110 280 Z"
                      fill="#22c55e"
                      stroke="#fff"
                      strokeWidth="2"
                      className="hover:opacity-80 cursor-pointer"
                    />
                    <text x="195" y="290" textAnchor="middle" className="fill-white text-xs font-bold">91</text>
                    <text x="195" y="302" textAnchor="middle" className="fill-white text-xs">Essonne</text>

                  </svg>
                </div>
                
                {/* Légende */}
                <div className="mt-8 flex justify-center space-x-8">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">Actuellement disponible</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-amber-500 rounded"></div>
                    <span className="text-sm font-medium text-gray-700">Ouverture prévue</span>
                  </div>
                </div>
              </div>

              {/* Informations détaillées */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Notre couverture territoriale
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    En tant qu'AMO habilité par l'ANAH, nous intervenons actuellement dans 6 départements 
                    franciliens et étendons progressivement notre couverture pour accompagner toujours plus 
                    de bénéficiaires dans leurs projets d'adaptation.
                  </p>
                </div>

                {/* Départements habilités */}
                <Card className="border-green-200 bg-green-50">
                  <CardHeader>
                    <CardTitle className="text-green-800 flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Départements actuellement habilités
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center">
                        <span className="font-medium text-green-700">75</span>
                        <span className="ml-2 text-gray-700">Paris</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-green-700">77</span>
                        <span className="ml-2 text-gray-700">Seine-et-Marne</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-green-700">91</span>
                        <span className="ml-2 text-gray-700">Essonne</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-green-700">92</span>
                        <span className="ml-2 text-gray-700">Hauts-de-Seine</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-green-700">93</span>
                        <span className="ml-2 text-gray-700">Seine-Saint-Denis</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-green-700">95</span>
                        <span className="ml-2 text-gray-700">Val-d'Oise</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Départements prochainement */}
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="text-orange-800 flex items-center">
                      <Clock className="h-5 w-5 mr-2" />
                      Prochainement habilités
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center">
                        <span className="font-medium text-orange-700">60</span>
                        <span className="ml-2 text-gray-700">Oise</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-orange-700">78</span>
                        <span className="ml-2 text-gray-700">Yvelines</span>
                      </div>
                      <div className="flex items-center">
                        <span className="font-medium text-orange-700">94</span>
                        <span className="ml-2 text-gray-700">Val-de-Marne</span>
                      </div>
                    </div>
                    <p className="text-sm text-orange-700 mt-4 font-medium">
                      Habilitations en cours - Contact possible dès maintenant
                    </p>
                  </CardContent>
                </Card>

                {/* CTA */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl">
                  <h4 className="text-lg font-bold mb-2">
                    Votre département n'est pas encore couvert ?
                  </h4>
                  <p className="text-blue-100 mb-4">
                    Contactez-nous pour connaître nos prochaines extensions territoriales
                  </p>
                  <Button 
                    className="bg-white text-blue-600 hover:bg-blue-50"
                    onClick={() => window.location.href = 'mailto:amo@transition-energie-france.fr?subject=Extension territoire'}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Nous contacter
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section transmettre un dossier */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Vous avez un client à nous transmettre ?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Orientez vos clients vers MaPrimeAdapt&apos; en 3 étapes simples
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-700 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                  1
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Testez l&apos;éligibilité</h3>
                <p className="text-sm text-gray-700">Complétez le simulateur avec les informations de votre client</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-green-700 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                  2
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Indiquez la source</h3>
                <p className="text-sm text-gray-700">Mentionnez votre entreprise dans le formulaire</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-green-700 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-3">
                  3
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Prise de contact</h3>
                <p className="text-sm text-gray-700">Nous contactons le client dans la journée</p>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => window.open('/simulateur', '_blank')}
            >
              <Users className="mr-2 h-5 w-5" />
              Tester l&apos;éligibilité d&apos;un client
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                FAQ Artisans Partenaires
              </h2>
              <p className="text-xl text-gray-700">
                Les questions les plus fréquentes sur notre partenariat
              </p>
            </div>
            
            <div className="space-y-6">
              {faqItems.map((item, index) => (
                <Card key={index} className="border border-gray-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-gray-900">
                      {item.question}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">{item.reponse}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      </main>

      <Footer />
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        type="general"
      />

    </div>
  );
};

export default AccesPro;